import type {
  AfPriceBatchOperation,
  AfPriceChangeInput,
  AfPriceLogAction,
  AfBillingBasis,
  AfPriceCurrency,
  AfPriceEventOption,
  AfPriceExpectedState,
  AfPriceRow,
} from '@/api';

export const OPEN_END_DATE = '2099-12-31';
export const EVENT_LOOKUP_MAX_DAYS = 31;
export const EVENT_OPTION_DISPLAY_LIMIT = 100;
export const NO_CAMPAIGN_FILTER = '__no_campaign__';

export type PriceOs = 'aos' | 'ios';

// 부가세율 — 행에는 포함 여부만 저장하고 세율은 백엔드 VAT_RATE와 같은 값을 쓴다.
export const VAT_RATE = 0.1;
// 신규 행 기본 통화. 국내 매체 정산이 대부분이라 KRW·부가세 10% 추가가 기본이다.
export const DEFAULT_NEW_CURRENCY: AfPriceCurrency = 'KRW';

const CURRENCY_SYMBOL: Record<AfPriceCurrency, string> = { KRW: '₩', JPY: '¥', USD: '$' };
// 원·엔은 정수로 입력받지만 부가세 10%를 더하면 소수 한 자리가 생긴다(122원 → 134.2원). 소수 2자리까지 보인다.
const CURRENCY_FRACTION_DIGITS: Record<AfPriceCurrency, number> = { KRW: 2, JPY: 2, USD: 8 };

/** 통화별 기본 부가세 포함 여부. 국내 매체(KRW)는 부가세 10% 추가가 기본, 나머지는 포함. 토글로 바꿀 수 있다. */
export function defaultVatIncluded(currency: AfPriceCurrency): boolean {
  return currency !== 'KRW';
}

/** 통화 기호 + 자릿수 정리. 예: formatMoney('2300', 'KRW') → '₩2,300'. */
export function formatMoney(value: string | number, currency: AfPriceCurrency): string {
  const amount = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(amount)) return '-';
  const formatted = new Intl.NumberFormat('ko-KR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: CURRENCY_FRACTION_DIGITS[currency],
  }).format(amount);
  return `${CURRENCY_SYMBOL[currency]}${formatted}`;
}

/** "입력 / BI 반영" 한 줄 표기. 부가세 포함이라 두 값이 같으면 한 번만 쓴다. */
export function formatInputAndApplied(
  unitPrice: string,
  currency: AfPriceCurrency,
  vatIncluded: boolean,
): string {
  if (!(Number(unitPrice) > 0)) return '-';
  const input = formatMoney(unitPrice, currency);
  const applied = formatMoney(biUnitPrice(unitPrice, vatIncluded), currency);
  return applied === input ? input : `${input} / ${applied}`;
}

/** BI에 반영되는 단가 — 부가세 10%를 더하는 행이면 (1 + 세율)을 곱한다. 통화는 그대로. */
export function biUnitPrice(unitPrice: string, vatIncluded: boolean): number {
  const amount = Number(unitPrice);
  if (!Number.isFinite(amount)) return 0;
  const factor = vatIncluded ? 1 : 1 + VAT_RATE;
  // 원·엔은 정수, 달러는 소수 8자리까지 — 부동소수 오차만 걷어낸다.
  return Math.round(amount * factor * 1e8) / 1e8;
}

// utils.apps store_id → AppsFlyer app_id. AOS=패키지명 그대로, iOS='id'+App Store 숫자.
export function afAppIdFromStoreId(storeId: string | null | undefined, os: PriceOs): string | null {
  const normalized = storeId?.trim();
  if (!normalized) return null;
  if (os === 'ios' && !normalized.toLowerCase().startsWith('id')) return `id${normalized}`;
  return normalized;
}

export interface StageForm {
  media_source: string;
  project_id: string;
  os: PriceOs;
  country: string;
  campaign_name: string;
  event_name: string;
  billing_basis: AfBillingBasis;
  unit_price: string;
  currency: AfPriceCurrency;
  vat_included: boolean;
  start_date: string;
  end_open: boolean;
  end_date: string;
  lookup_start_date: string;
  lookup_end_date: string;
}

export interface StagedRow {
  opId: string;
  kind: 'insert' | 'update';
  fixedMediaSource: boolean;
  seriesId: string | null;
  original: AfPriceRow | null;
  form: StageForm;
  campaignOptions: string[];
  eventOptions: AfPriceEventOption[];
  optionsLoaded: boolean;
  // 기존 행(update)만 삭제 예정으로 표시할 수 있다. 실제로는 off 로 바뀌는 update 요청이다.
  deleted?: boolean;
}

function localIso(value: Date): string {
  const local = new Date(value.getTime() - value.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 10);
}

export function today(): string {
  return localIso(new Date());
}

function daysAgo(days: number): string {
  const value = new Date();
  value.setDate(value.getDate() - days);
  return localIso(value);
}

export function currentMonth(): string {
  return today().slice(0, 7);
}

export function shiftDate(value: string, days: number): string {
  if (!value) return '';
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return '';
  date.setDate(date.getDate() + days);
  return localIso(date);
}

export function eventLookupWindow(
  form: Pick<StageForm, 'start_date' | 'end_open' | 'end_date'>,
): { start: string; end: string } | null {
  const currentDate = today();
  const end = form.end_open || form.end_date > currentDate ? currentDate : form.end_date;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(form.start_date) || !/^\d{4}-\d{2}-\d{2}$/.test(end)) {
    return null;
  }

  const clampedStart = shiftDate(end, -(EVENT_LOOKUP_MAX_DAYS - 1));
  const start = form.start_date > end || form.start_date < clampedStart
    ? clampedStart
    : form.start_date;
  return { start, end };
}

export function shiftMonth(value: string, months: number): string {
  const [year, month] = value.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1 + months, 1)).toISOString().slice(0, 7);
}

export function monthBounds(value: string): { start: string; end: string } {
  const [year, month] = value.split('-').map(Number);
  return {
    start: new Date(Date.UTC(year, month - 1, 1)).toISOString().slice(0, 10),
    end: new Date(Date.UTC(year, month, 0)).toISOString().slice(0, 10),
  };
}

export function createForm(projectId: string, os: PriceOs, row?: AfPriceRow): StageForm {
  const openEnded = !row || row.end_date === OPEN_END_DATE;
  return {
    media_source: row?.media_source ?? '',
    project_id: projectId,
    os,
    country: row?.country ?? '',
    campaign_name: row?.campaign_name ?? '',
    event_name: row?.event_name ?? '',
    billing_basis: row?.billing_basis ?? 'event',
    unit_price: row?.unit_price ?? '',
    currency: row?.currency ?? DEFAULT_NEW_CURRENCY,
    vat_included: row?.vat_included ?? defaultVatIncluded(DEFAULT_NEW_CURRENCY),
    start_date: row?.start_date ?? today(),
    end_open: openEnded,
    end_date: openEnded ? (row?.start_date && row.start_date > today() ? row.start_date : today()) : row.end_date,
    lookup_start_date: daysAgo(EVENT_LOOKUP_MAX_DAYS - 1),
    lookup_end_date: today(),
  };
}

export function normalizeCountry(value: string): string {
  return value.replace(/[^a-z]/gi, '').toUpperCase().slice(0, 2);
}

export function isTwoLetterCountry(value: string): boolean {
  return /^[A-Za-z]{2}$/.test(value);
}

export function filterComboboxOptions(options: string[], query: string): string[] {
  const normalizedQuery = query.trim().toLocaleLowerCase();
  if (!normalizedQuery) return options;
  return options.filter((option) => option.toLocaleLowerCase().includes(normalizedQuery));
}

export function toChangeInput(form: StageForm, reason: string | null): AfPriceChangeInput {
  return {
    media_source: form.media_source,
    project_id: form.project_id,
    os: form.os,
    country: form.country || null,
    campaign_name: form.campaign_name || null,
    event_name: form.event_name,
    billing_basis: form.billing_basis,
    unit_price: form.unit_price,
    currency: form.currency,
    vat_included: form.vat_included,
    start_date: form.start_date,
    end_date: form.end_open ? null : form.end_date,
    change_reason: reason,
    is_active: true,
  };
}

export function toExpected(row: AfPriceRow): AfPriceExpectedState {
  return {
    series_id: row.series_id,
    media_source: row.media_source,
    app_id: row.app_id,
    os: row.os,
    country: row.country,
    campaign_name: row.campaign_name,
    event_name: row.event_name,
    billing_basis: row.billing_basis ?? 'event',
    unit_price: row.unit_price,
    currency: row.currency,
    vat_included: row.vat_included,
    start_date: row.start_date,
    end_date: row.end_date,
    changed_at: row.changed_at,
    is_active: row.is_active ?? true,
  };
}

export function isValidStage(stage: StagedRow): boolean {
  // 삭제는 현재 값을 그대로 보내므로 입력 검증 대상이 아니다.
  if (stage.deleted) return true;
  const { form } = stage;
  return form.media_source.trim().length > 0
    && form.project_id.length > 0
    && (form.country.length === 0 || isTwoLetterCountry(form.country))
    && (form.billing_basis === 'install'
      ? form.campaign_name.trim().length > 0
      : form.event_name.trim().length > 0)
    && Number(form.unit_price) > 0
    && (form.currency === 'USD' || Number.isInteger(Number(form.unit_price)))
    && form.start_date.length === 10
    && (form.end_open || (form.end_date.length === 10 && form.end_date >= form.start_date));
}

const DIFF_FIELDS: Array<{ key: keyof StageForm; label: string }> = [
  { key: 'media_source', label: '미디어소스' },
  { key: 'campaign_name', label: '캠페인' },
  { key: 'event_name', label: '이벤트' },
  { key: 'billing_basis', label: '과금 기준' },
  { key: 'country', label: '국가' },
  { key: 'unit_price', label: '단가 (입력 / BI 반영)' },
  { key: 'vat_included', label: '부가세' },
  { key: 'start_date', label: '적용 시작일' },
  { key: 'end_date', label: '적용 마지막일' },
];

export function formatVatIncluded(vatIncluded: boolean): string {
  return vatIncluded ? '포함' : `부가세 ${Math.round(VAT_RATE * 100)}%`;
}

/** 변경내역 diff에 보이는 값. 단가는 통화와 함께, 부가세는 말로 풀어 쓴다. */
function diffValue(form: StageForm, key: keyof StageForm): string {
  if (key === 'event_name' && form.billing_basis === 'install') return '지정하지 않음';
  if (key === 'billing_basis') return form.billing_basis === 'install' ? '설치수 × 단가' : '이벤트수 × 단가';
  if (key === 'end_date') return form.end_open ? '미정' : form.end_date || '-';
  if (key === 'unit_price') return formatInputAndApplied(form.unit_price, form.currency, form.vat_included);
  if (key === 'vat_included') return formatVatIncluded(form.vat_included);
  return String(form[key] || '-');
}

export function stageDiffs(stage: StagedRow): Array<{ label: string; before: string; after: string }> {
  if (!stage.original) {
    return DIFF_FIELDS.map(({ key, label }) => ({
      label,
      before: '-',
      after: diffValue(stage.form, key),
    }));
  }
  const before = createForm(stage.form.project_id, stage.form.os, stage.original);
  return DIFF_FIELDS.flatMap(({ key, label }) => {
    const beforeValue = diffValue(before, key);
    const afterValue = diffValue(stage.form, key);
    return beforeValue === afterValue ? [] : [{ label, before: beforeValue, after: afterValue }];
  });
}

/** 삭제(off) 요청: 현재 행 값을 그대로 두고 is_active 만 false 로 바꾸는 update. */
export function toDeleteChange(row: AfPriceRow, projectId: string): AfPriceChangeInput {
  return {
    ...toChangeInput(createForm(row.project_id ?? projectId, row.os, row), row.change_reason ?? null),
    is_active: false,
  };
}

const COMPARED_INPUT_FIELDS = [
  'media_source',
  'os',
  'country',
  'campaign_name',
  'event_name',
  'billing_basis',
  'unit_price',
  'currency',
  'vat_included',
  'start_date',
] as const;

type ComparedInputField = (typeof COMPARED_INPUT_FIELDS)[number];

function comparedInputValue(field: ComparedInputField, value: unknown): unknown {
  if (field === 'billing_basis') return value ?? 'event';
  if (field === 'unit_price') {
    const amount = Number(value);
    return Number.isFinite(amount) ? amount : value;
  }
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  const normalized = field === 'media_source' || field === 'country' ? trimmed.toLowerCase() : trimmed;
  return normalized || null;
}

function openEndToNull(value: string | null | undefined): string | null {
  return !value || value === OPEN_END_DATE ? null : value;
}

/**
 * 요청에 담긴 작업 한 건의 종류. 백엔드 작업 로그(_log_action)와 같은 기준이다:
 * 종료는 종료일만 바뀌고 나머지 입력값이 그대로일 때다.
 */
export function priceOperationAction(operation: AfPriceBatchOperation): AfPriceLogAction {
  if (operation.type === 'insert') return 'insert';
  const { change, expected } = operation;
  if (expected.is_active !== false && change.is_active === false) return 'delete';
  const sameInputs = COMPARED_INPUT_FIELDS.every(
    (field) => comparedInputValue(field, change[field]) === comparedInputValue(field, expected[field]),
  );
  const afterEnd = openEndToNull(change.end_date);
  if (sameInputs && afterEnd !== null && afterEnd !== openEndToNull(expected.end_date)) return 'close';
  return 'update';
}

export const PRICE_ACTION_LABEL: Record<AfPriceLogAction, string> = {
  insert: '추가',
  update: '수정',
  close: '종료',
  delete: '삭제(off)',
};
